// Settings area — SettingsPage.tsx + components/settings/* tabs.
export const settings = {
  badges: {
    configured: "Configured",
    notSet: "Not set",
    savedTypeToReplace: "•••••••••• (saved — type to replace)",
  },
  page: {
    title: "Settings",
    subtitle: "Manage website and system settings",
    loadFailed: "Failed to load settings",
    loadFailedRefresh: "Failed to load settings. Please refresh the page.",
    saved: "Settings saved",
    saveFailed: "Failed to save settings",
    unsavedConfirm: "You have unsaved changes. Continue without saving?",
    unsavedChanges: "You have unsaved changes",
    saveChanges: "Save Changes",
    tabsAriaLabel: "Tabs",
    tabs: {
      company: "Company",
      smtp: "SMTP",
      notifications: "Notifications",
      booking: "Bookings",
      seo: "SEO & Analytics",
      social: "Social Media",
      security: "Security",
      ai: "AI (ChatGPT)",
      marketing: "Marketing & Tracking",
      content: "Site content",
      finance: "Finance",
      cache: "Cache",
    },
  },
  company: {
    title: "Company Information",
    companyName: "Company Name",
    businessId: "Business ID",
    taxId: "Tax ID",
    licenseNumber: "Travel Agency License Number",
    licenseNumberPlaceholder: "E.g. 123456/2020",
    address: "Business Address",
    contactEmail: "Contact Email",
    phone: "Phone",
    bankDetailsTitle: "Bank Details",
    accountNumber: "Account Number",
    iban: "IBAN",
    swift: "SWIFT/BIC",
    bankName: "Bank Name",
    googleReviewsTitle: "Google Reviews",
    rating: "Rating",
    reviewCount: "Review Count",
    reviewUrl: "Review URL",
    distributionTitle: "Rating distribution",
    distributionHint:
      "Five numbers copied from the Google profile. The chart appears on the website only when all five are filled in; zero is a valid value.",
    stars5: "5 stars",
    stars4: "4 stars",
    stars3: "3 stars",
    stars2: "2 stars",
    stars1: "1 star",
    distributionSum: "Distribution sum",
    distributionSumIncomplete: "not filled in",
    distributionEnteredTotal: "entered total count",
    googleManualFallbackHint:
      "These numbers apply on the website only until the first uploaded Takeout export (Google reviews → Upload export). After that the summary from the export takes precedence and these numbers stay as a fallback.",
  },
  smtp: {
    bulkInfoTitle: "Bulk newsletter transport.",
    bulkInfoBody:
      "Transactional mail (order/inquiry confirmations) is sent via the connected Gmail mailbox — this SMTP is used for the mass newsletter. Save your settings first, then send a test. Tip: use a sender on a domain configured at your SMTP host (SPF/DKIM) for good deliverability.",
    serverTitle: "SMTP Server",
    host: "SMTP Host",
    port: "Port",
    port587: "587 (STARTTLS - recommended)",
    port465: "465 (SSL)",
    port25: "25 (unencrypted - blocked on Cloudflare)",
    port2525: "2525 (alternative)",
    username: "Username",
    password: "Password",
    storedEncrypted: "Stored encrypted; never shown again after save.",
    encryption: "Encryption",
    encryptionTls: "TLS",
    encryptionSsl: "SSL",
    encryptionNone: "None",
    senderTitle: "Sender",
    senderName: "Sender Name",
    senderEmail: "Sender Email (From)",
    replyTo: "Reply-To Email",
    sendTest: "Send Test Email",
    sending: "Sending…",
    sendTestHint: "Sends to your account email via the saved SMTP.",
    serverConnectionError: "Server connection error",
  },
  notifications: {
    title: "Email Notifications",
    items: {
      newOrder: "New Order",
      newOrderDesc: "When a new order is created",
      orderPaid: "Payment Received",
      orderPaidDesc: "When a customer pays",
      orderCancelled: "Order Cancelled",
      orderCancelledDesc: "When an order is cancelled",
      newInquiry: "New Inquiry",
      newInquiryDesc: "When a non-binding inquiry arrives",
      newReview: "New Review",
      newReviewDesc: "When a customer writes a review",
      lowCapacity: "Low Capacity",
      lowCapacityDesc: "When fewer than 3 spots remain",
    },
    recipientsTitle: "Notification Recipients",
    recipientsLabel: "Emails (comma-separated)",
    slackTitle: "Slack Integration",
    webhookUrl: "Webhook URL",
  },
  booking: {
    title: "Booking Settings",
    depositPercent: "Deposit Amount (%)",
    depositDueDays: "Deposit Due (days)",
    finalPaymentDaysBefore: "Final Payment (days before departure)",
    minParticipants: "Min. Participants",
    maxParticipants: "Max. Participants",
    cancellationTitle: "Cancellation Policy",
    daysRange: "{{range}} days",
    percentOfPrice: "% of price",
  },
  seo: {
    metaTitle: "Meta Tags (default)",
    siteTitle: "Site Title",
    metaDescription: "Description (meta description)",
    keywords: "Keywords",
    verificationTitle: "Search Console / Site verification",
    verificationHint:
      "Paste the verification token (just the content value, not the whole meta tag) — it renders into every page's <head>. The live GA4 measurement ID lives in the Marketing tab.",
    googleSearchConsole: "Google Search Console",
    googleSearchConsolePlaceholder: "google-site-verification token",
    bingWebmaster: "Bing Webmaster (optional)",
    bingWebmasterPlaceholder: "msvalidate.01 token",
    pinterest: "Pinterest (optional)",
    pinterestPlaceholder: "p:domain_verify token",
    googleApiTitle: "Google API (for Analytics Dashboard)",
    infoTitle: "Info:",
    infoBody:
      "These credentials are used to fetch data from Google Analytics 4 and Google Ads directly into the admin dashboard. Requires a Service Account with access to the GA4 property and Google Ads account.",
    ga4PropertyId: "GA4 Property ID",
    ga4PropertyIdHint: "Found in GA4 > Admin > Property Settings",
    googleAdsCustomerId: "Google Ads Customer ID",
    googleAdsCustomerIdHint: "Found in Google Ads > top menu (format: XXX-XXX-XXXX)",
    googleAdsDeveloperToken: "Google Ads Developer Token",
    googleAdsDeveloperTokenHint:
      "Obtained from Google Ads > Tools > API Center (requires approval)",
    serviceAccountJson: "Google Service Account JSON",
    serviceAccountJsonHint:
      "JSON key from Service Account in Google Cloud Console. The Service Account must have access to the GA4 property (Admin > Property Access Management) and Google Ads account (Admin > Linked accounts).",
  },
  social: {
    title: "Social Media",
    podcastSync: {
      title: "Podcasts (YouTube sync)",
      description:
        "Optional add-on. Episodes can be managed by hand without these — without a playlist id or a key nothing simply gets synced.",
      playlistIdLabel: "YouTube playlist ID",
      apiKeyLabel: "API key (YouTube Data API v3)",
      apiKeyHint:
        "The key is stored encrypted and never displayed. An empty field does NOT clear it — it just leaves it unchanged.",
      syncNow: "Sync now",
      notConfigured: "Sync is not configured (missing playlist ID or API key) — nothing changed.",
      syncDone: "Done: {{fetched}} episodes in the playlist, {{created}} new, {{updated}} updated.",
    },
  },
  security: {
    title: "Security",
    autoLogout: "Auto-logout after (minutes)",
    minutes30: "30 minutes",
    hour1: "1 hour",
    hours2: "2 hours",
    hours8: "8 hours",
    hours24: "24 hours",
    items: {
      require2fa: "Two-Factor Authentication (2FA)",
      require2faDesc: "Require 2FA for all administrators",
      logAccess: "Access Logging",
      logAccessDesc: "Record all logins and administrator actions",
      emailOnNewLogin: "Email on New Login",
      emailOnNewLoginDesc: "Send email when logging in from a new device",
    },
    passwordPolicyTitle: "Password Policy",
    minPasswordLength: "Minimum Password Length",
    passwordExpiration: "Password Expiration (days, 0 = never)",
  },
  ai: {
    test: "Test",
    connectionWorks: "{{provider}} connection works",
    connectFailed: "Failed to connect to {{provider}}",
    anthropicTitle: "Anthropic / Claude — “MAKEJ!” agent",
    anthropicDesc:
      "Powers the AI operations assistant (chat + contextual actions across the admin). Without a key the agent stays unavailable; everything else keeps working.",
    apiKeyLabel: "Anthropic API Key",
    apiKeyHelp:
      "Get your API key at <0>console.anthropic.com</0>. Stored encrypted — it’s never shown again after saving.",
    featuresTitle: "Features",
    segmentationLabel: "AI Customer Segmentation",
    segmentationDesc:
      'Allows creating customer segments using natural language (e.g. "customers who visited Bali last year")',
    personalizationLabel: "AI Email Personalization",
    personalizationDesc:
      "Automatically adjusts email content based on customer profile in automated campaigns",
    fourEyesLabel: "Four-eyes for MAKEJ! money actions",
    fourEyesDesc:
      "Require a second operator to approve agent cancellations, date changes, invoices and emails — whoever raised the proposal can’t approve their own. Leave off for a solo admin.",
    brandToneTitle: "MAKEJ! brand & tone",
    brandToneIntro:
      "Tune how the agent <0>sounds</0> — voice, language and sign-off — across chat and the contextual buttons. This is <1>tone only</1>: it can never change what the agent is allowed to do. The grounding and four-eyes rules always win, so it will still refuse to state a price it can’t verify or to send anything without approval.",
    languageLabel: "Language",
    languagePlaceholder: "čeština",
    languageHint:
      "The language MAKEJ! writes in. Empty means the default, Czech (čeština); you can enter another language, e.g. English or Vietnamese.",
    signOffLabel: "Sign-off",
    signOffPlaceholder: "— The CK Go2 team",
    signOffHint: "How drafts sign off (optional).",
    brandVoiceLabel: "Brand voice",
    brandVoicePlaceholder:
      "Warm, knowledgeable and unhurried — like a well-travelled friend. Confident but never pushy.",
    brandVoiceHint: "A short description of the tone you want in customer-facing drafts.",
    houseRulesLabel: "House rules",
    houseRulesPlaceholder:
      "Wording preferences only — e.g. always greet by first name, never use exclamation marks, prefer 'trip' over 'tour', offer the customer portal link when relevant.",
    houseRulesHint:
      "Extra wording/tone guidance. These cannot override the safety rules — the agent still won’t invent facts or act without approval.",
    agentEmailTitle: "Agent over email",
    agentEmailIntro:
      "Let a staff member email the agent (mentioning the trigger token) and get a reply — without opening the admin. It answers questions and prepares drafts, but <0>never executes anything</0>: every change still needs in-app approval by a signed-in person. Replies always go to the staff member’s account email on file.",
    emailChannelLabel: "Enable email channel",
    emailChannelDesc:
      "Off by default. When on, every 5 minutes the agent scans newly-synced inbox mail for the trigger token from a known staff sender and replies in-thread. Needs a connected Gmail mailbox.",
    triggerTokenLabel: "Trigger token",
    triggerTokenDesc:
      "Mention this anywhere in the subject or body to invoke the agent (e.g. <0>@makej kolik lidí je na odjezdu GO2-…?</0>).",
    readOnlyLabel: "Read-only mode (questions only)",
    readOnlyDesc:
      "The most conservative setting: over email the agent only answers questions and never drafts proposals. Leave off to let it also prepare drafts (which still require in-app approval).",
    requireAuthLabel: "Require authenticated sender (recommended)",
    requireAuthDesc:
      "Only act on mail that passes email authentication (DKIM aligned to the sender domain, or DMARC) — so a spoofed “from a staff address” email is ignored. Keep on unless your staff send from a domain without DKIM/DMARC.",
    triageTitle: "Inbound triage",
    triageIntro:
      "Classify each new contact-form message and cold email by <0>intent</0> — sales / question / supplier / noise. Every item gets an intent chip in the inbox for one-click triage. When auto-create is on, a <1>confident sales</1> inquiry is turned into a lead automatically (with an AI-drafted first reply waiting for review).",
    triageAutoCreateLabel: "Auto-create leads from confident sales inquiries",
    triageAutoCreateDesc:
      "Off by default (suggest → approve): items only get an intent chip you act on. Turn on once you trust the classification to let clear sales inquiries become leads on their own — questions, suppliers and spam are never auto-converted.",
    confidenceLabel: "Confidence threshold for auto-create",
    confidenceDesc:
      "Only auto-create a lead when the model is at least this sure the message is a sales inquiry. Higher = more conservative (fewer false leads). Applies only when auto-create is on.",
    leadRoutingTitle: "Lead auto-routing",
    leadRoutingIntro:
      "Assign fresh leads automatically: the matched destination's <0>garant</0> first, otherwise the active sales user with the fewest open leads (workload round-robin). Manual reassignment always stays possible. Individual destinations can opt out on their edit page — their leads then stay unassigned for manual sorting.",
    leadRoutingLabel: "Enable lead auto-routing",
    leadRoutingDesc:
      "Off by default — new leads stay unassigned until someone picks them up. Turn on to replace the manual Monday sorting.",
    entryTitle: "Entry auto-filing",
    entryIntro:
      "Files arrival cards on government portals automatically (cloud browser) once the filing window opens — per traveller, only with the client's portal consent and only for countries switched to the browser channel in Country KB. The QR/confirmation lands on the entry task and the client gets a portal link by email. Run a <0>dry run</0> from the departure's Entry documents panel first — it fills the whole form, stops before submit and stores a review screenshot.",
    entryLabel: "Enable entry autopilot",
    entryDesc:
      "Global kill-switch, off by default. Three consecutive failures in a country switch that country back to manual automatically (portal change protection).",
    callsTitle: "Phone calls",
    callsIntro:
      "Call transcripts that arrive by email (e.g. from a Plaud recorder) are recognised, summarised by AI and stored as phone calls on the customer/lead timeline — with a proposed follow-up you can apply in one click. A confident sales call from an unknown number becomes a lead under the same triage rules as email.",
    transcriptSendersLabel: "Transcript senders",
    transcriptSendersDesc:
      "Comma-separated domains or addresses whose email is always treated as a call transcript. Leave empty for the default (<0>plaud.ai</0>). Mail from other senders is still detected when the subject mentions a transcript and the body looks like a dialog.",
    webhookSecretLabel: "Call webhook secret",
    webhookSecretDesc:
      "For a future cloud PBX (CloudTalk): POST call transcripts to <0>/api/webhooks/calls</0> with this value in the <1>X-Webhook-Secret</1> header. The endpoint stays dark (404) until a secret is saved here.",
    webhookSecretPlaceholder: "a long random string",
  },
  marketing: {
    rotateConfirm: "Rotate the feed key? Surya will stop pulling until you paste the new key.",
    generateKeyFailed: "Failed to generate key",
    backfillDone: "Backfill done — {{inserted}} new of {{scanned}} paid orders.",
    backfillFailed: "Backfill failed",
    downloadFailed: "Download failed",
    consentTitle: "Cookie consent",
    consentDesc:
      "Controls when analytics & marketing tags may fire. Our own first-party tracking always runs (anonymous). The cookie banner shows on the public website.",
    consentModeLabel: "Consent mode",
    consentModeHint: "Geo: EU/EEA/UK visitors must opt in; everyone else is opt-out.",
    consentGeo: "Geo — EU opt-in, rest opt-out (recommended)",
    consentOptIn: "Opt-in everywhere (strict)",
    consentOptOut: "Opt-out everywhere",
    cookieBannerLabel: "Show cookie banner",
    cookieBannerDesc: "Display the consent banner on the public website.",
    freqCapTitle: "Frequency cap",
    freqCapDesc:
      "The cross-channel guardrail so one contact isn’t over-mailed. Every marketing send — newsletter, automatic campaigns and journeys — checks this before sending; it never applies to transactional mail (order confirmations, invoices).",
    freqCapEnforceLabel: "Enforce a frequency cap",
    freqCapEnforceDesc:
      "When off, marketing emails are limited only by consent (unsubscribes still apply).",
    maxPerWindowLabel: "Max per window",
    maxPerWindowHint: "Most marketing emails one contact may get per window.",
    windowDaysLabel: "Window (days)",
    windowDaysHint: "The rolling window the cap counts over.",
    minGapLabel: "Min gap (hours)",
    minGapHint: "Minimum time between two marketing emails. 0 = off.",
    googleTitle: "Google",
    googleDesc:
      "GA4 analytics + Google Ads conversion tracking. Server-side upload (gclid) reuses the Google Ads API credentials from the SEO & Analytics tab.",
    ga4MeasurementIdLabel: "GA4 Measurement ID",
    ga4MeasurementIdHint: "e.g. G-XXXXXXXXXX",
    adsConversionIdLabel: "Google Ads Conversion ID",
    adsConversionIdHint: "e.g. AW-123456789",
    conversionLabelLabel: "Conversion Label",
    conversionLabelHint: "The purchase conversion action label.",
    serverSideLabel: "Server-side conversions",
    serverSideDesc:
      "Upload paid conversions via the Google Ads API (gclid). Needs the API credentials in SEO & Analytics + the conversion action id below.",
    conversionActionIdLabel: "Conversion Action ID (server-side)",
    conversionActionIdHint:
      "Numeric id of the Google Ads conversion action — for the gclid upload, not the AW-… above.",
    metaTitle: "Meta (Facebook / Instagram)",
    metaDesc:
      "Pixel (browser) + Conversions API (server-side) for reliable conversions. The CAPI token is stored encrypted and never shown again.",
    metaPixelIdLabel: "Meta Pixel ID",
    metaPixelIdHint: "Numeric pixel id.",
    testEventCodeLabel: "Test Event Code",
    testEventCodeHint: "Optional — for Meta Events Manager test traffic.",
    capiTokenLabel: "Meta CAPI Access Token",
    capiTokenHint: "Conversions API token from Meta Events Manager. Stored encrypted.",
    feedTitle: "Conversion feed (PPC)",
    feedDesc:
      "Read-only feed of paid orders — which trip sold + real revenue & margin + click-id — for your PPC platform (Surya) to pull, so its agents optimise on profit. Ad spend & ROAS stay in Surya; this feed exposes no personal data.",
    feedUrlLabel: "Feed URL (pull endpoint)",
    feedUrlHint:
      "Surya pulls this with the API key as a Bearer token. Supports ?since=<cursor> for incremental pulls and ?format=csv.",
    copyUrl: "Copy URL",
    feedKeyLabel: "Feed API Key",
    feedKeyHint:
      "The Bearer token Surya presents. Generated server-side, stored encrypted, shown only once.",
    rotateKey: "Rotate key",
    generateKey: "Generate key",
    copyNow: "Copy this now — it won't be shown again:",
    copyKey: "Copy key",
    backfillButton: "Backfill from paid orders",
    downloadCsv: "Download CSV",
  },
  finance: {
    loadFailed: "Failed to load finance settings",
    numberingSaved: "Numbering for {{scope}} saved",
    numberingSaveFailed: "Failed to save numbering",
    configSaved: "Finance configuration saved",
    configSaveFailed: "Failed to save finance configuration",
    scopeOrders: "Orders",
    scopeLeads: "Inquiries (leads)",
    configTitle: "Finance configuration",
    configDesc:
      "Reporting currency, exchange rates (used to snapshot foreign amounts into CZK), VAT, and how long passport data is kept after departure. Deposit %, payment terms and cancellation tiers live under <0>Booking</0>; bank account and IČO/DIČ under <1>Company</1>.",
    defaultCurrency: "Default currency",
    vatRate: "VAT rate (%)",
    paymentQr: "Payment QR (portal)",
    qrAuto: "Auto (EUR → EPC, CZK → SPAYD)",
    qrOff: "Off (bank details only)",
    passportRetention: "Passport retention (days)",
    eventRetention: "Event retention (days)",
    eventRetentionHint:
      "GDPR: raw behavioural events are deleted after this window (≈425 = 14 months).",
    ratesTitle: "Exchange rates (1 unit → CZK)",
    addCurrency: "Add currency",
    saveConfig: "Save configuration",
    numberingTitle: "Document numbering",
    numberingDesc:
      "Set the prefix and the next number for each document series. Counters reset per year (the year is part of the series). Use “Next number” to continue an existing series — e.g. set it to 100 and the next order becomes <0>GO2-{{year}}-0100</0>.",
    prefix: "Prefix",
    nextNumber: "Next number",
    preview: "Preview",
  },
  cache: {
    infoTitle: "Cache Information",
    infoDesc:
      "The website uses 5-minute caching for faster loading. Admin changes will appear automatically within 5 minutes, or you can clear the cache manually.",
    clearTitle: "Clear Cache",
    clearDesc: "Clears all cached data and forces refresh from database",
    clearing: "Clearing…",
    cleared: "Cache cleared successfully",
    clearFailed: "Failed to clear cache",
    statsTitle: "Statistics",
    ttlLabel: "Cache TTL",
    regionLabel: "Region",
    regionGlobal: "Global",
  },
  content: {
    title: "Site content",
    planningBlockTitle: "Planning block variants",
    planningBlockDescription:
      "Text shown on a destination page instead of the default sentence when the destination has no trip. The destination chooses the variant in its Offer tab. Plain text; empty = the website uses the default wording.",
    variantLabel: "Variant {{n}}",
    reviewOriginTitle: "Review origin texts",
    reviewOriginDescription:
      "The website uses these texts next to reviews. Empty field = default wording (shown in grey).",
    reviewOriginLabels: {
      reviewsIntro: "Introduction",
      reviewsOriginClients: "From clients",
      reviewsOriginGoogle: "On Google",
      reviewsOriginElsewhere: "Elsewhere on the website",
    },
    placeholdersTitle: "Placeholders you can use in the texts",
    placeholders: {
      score: "Google rating (e.g. 4.9)",
      count: "number of Google reviews",
      share: "share of five-star reviews in percent",
      date: "date of the Google summary",
    },
    googleSummaryTitle: "Google summary",
    googleSummarySource: "Source",
    googleSummarySourceExport: "Takeout export ({{name}})",
    googleSummarySourceManual: "Manual numbers from the Company tab",
    googleSummaryScore: "Rating",
    googleSummaryCount: "Review count",
    googleSummaryDate: "Date",
    googleSummaryNoExport:
      "Until the first uploaded export, the manual numbers from the Company tab apply.",
    googleSummaryWithExport:
      "The summary is calculated from the last confirmed export; the manual numbers stay as a fallback.",
    googleSummaryLoadError: "The Google summary could not be loaded.",
  },
};
